/**
 * Clerk Token Manager
 * 
 * Manages Clerk authentication tokens for API requests.
 * Provides a way to set tokens from React components using useAuth hook
 * and retrieve them in the axios interceptor.
 */

let currentToken = null;
let tokenPromise = null;

/**
 * Set the current Clerk token
 * Call this from React components using the useAuth hook
 */
export function setClerkToken(token) {
  currentToken = token;
}

/**
 * Get the current Clerk token
 * Used by axios interceptor
 */
export function getClerkToken() {
  return currentToken;
}

/**
 * Wait for Clerk to be ready (with timeout)
 */
async function waitForClerk(maxWaitMs = 3000) {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    if (window.Clerk?.session?.getToken) {
      return true;
    }
    // Wait 100ms before checking again
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return false;
}

/**
 * Get token with fallback methods for compatibility
 * Tries multiple approaches to get a valid Clerk token
 * ALWAYS gets a fresh token to avoid expiration issues (Clerk tokens expire in ~60s)
 */
export async function getClerkTokenWithFallback() {
  // Prevent multiple simultaneous token fetches
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    try {
      if (typeof window === 'undefined') {
        return null;
      }

      // Wait for Clerk to be ready if it's not yet
      if (!window.Clerk?.session?.getToken) {
        await waitForClerk(3000);
      }

      let token = null;

      // Always get a fresh token - Clerk tokens expire quickly (~60 seconds)
      // Method 1: Try window.Clerk.session.getToken() - most reliable
      if (window.Clerk?.session?.getToken) {
        token = await window.Clerk.session.getToken({ skipCache: true });
      }
      // Method 2: Check if Clerk is loaded and has a session
      else if (window.Clerk?.loaded && window.Clerk?.session) {
        token = await window.Clerk.session.getToken({ skipCache: true });
      }
      // Method 3: Try unstable environment method as last resort
      else if (window.Clerk?.__unstable__environment?.getSessionToken) {
        token = await window.Clerk.__unstable__environment.getSessionToken();
      }

      if (token) {
        currentToken = token;
      }

      return token;
    } catch (error) {
      console.error('[ClerkTokenManager] Error fetching token:', error);
      return null;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}

/**
 * Clear the cached token
 * Call this on sign out
 */
export function clearClerkToken() {
  currentToken = null;
}
